import { TRPCClientError } from "@trpc/client";
import {
  PSV_STATE_SCOPED_SOURCE_FAMILIES,
  PsvMonitoringStateScope,
  PsvResolvedScopeKind,
  PsvSourceFamily,
} from "@api/shared-runtime/psv-monitoring";
import { Temporal } from "temporal-polyfill";
import {
  CONFLICT_ERROR_CODE,
  FORBIDDEN_ERROR_CODE,
  MONITORING_COPY,
  SOURCE_LABELS,
  SOURCE_SHORT_LABELS,
  SUMMARY_SOURCE_LIMIT,
} from "./monitoring-constants";
import type {
  MonitoringProgram,
  ResolvedMonitorScope,
  SourceSummary,
} from "./monitoring-types";

const PROVIDER_ID_SUFFIX_LENGTH = 4;

function errorCode(error: unknown): unknown {
  if (!(error instanceof TRPCClientError)) {
    return null;
  }
  return error.data?.code;
}

export function isForbiddenError(error: unknown): boolean {
  return errorCode(error) === FORBIDDEN_ERROR_CODE;
}

export function isConflictError(error: unknown): boolean {
  return errorCode(error) === CONFLICT_ERROR_CODE;
}

export function plural({ count, noun }: { count: number; noun: string }) {
  return `${count} ${noun}${count === 1 ? "" : "s"}`;
}

export function formatMonitoringDate(value: string | Date): string {
  const instant = Temporal.Instant.from(
    value instanceof Date ? value.toISOString() : value,
  );
  return instant.toLocaleString("en-US", { dateStyle: "medium" });
}

/** Preview ids are not reveal-aware yet, so show a short anonymous label. */
export function providerIdLabel(providerUserId: string): string {
  return `Provider #${providerUserId.slice(-PROVIDER_ID_SUFFIX_LENGTH)}`;
}

export function sourceSelectionLabel({
  source,
  short,
}: {
  source: SourceSummary;
  short: boolean;
}): string {
  const labels = short ? SOURCE_SHORT_LABELS : SOURCE_LABELS;
  const label = labels[source.family];
  if (!PSV_STATE_SCOPED_SOURCE_FAMILIES.includes(source.family)) {
    return label;
  }
  if (source.stateScope === PsvMonitoringStateScope.Selected) {
    return `${label} (${source.stateCodes.join(", ")})`;
  }
  return `${label} (all states)`;
}

function limitLabels(labels: string[]): string {
  const shown = labels.slice(0, SUMMARY_SOURCE_LIMIT);
  const hidden = labels.length - shown.length;
  if (hidden > 0) {
    shown.push(`+${hidden}`);
  }
  return shown.join(", ");
}

/** "State licenses (CA, TX), DEA, NPI, +2" with a fixed number of entries. */
export function summarizeSources(sources: readonly SourceSummary[]): string {
  if (sources.length === 0) {
    return "No sources";
  }
  return limitLabels(
    sources.map((source) => sourceSelectionLabel({ source, short: true })),
  );
}

export function summarizeResolvedScope(scope: ResolvedMonitorScope): string {
  return limitLabels(
    scope.resolved.map(({ family, scope: familyScope }) => {
      const label = SOURCE_SHORT_LABELS[family];
      if (familyScope.kind === PsvResolvedScopeKind.States) {
        return `${label} (${familyScope.stateCodes.join(", ")})`;
      }
      if (familyScope.kind === PsvResolvedScopeKind.AllStates) {
        return `${label} (all states)`;
      }
      return label;
    }),
  );
}

export function summarizeTargets(targets: MonitoringProgram["targets"]) {
  const parts: string[] = [];
  if (targets.providerPools.length > 0) {
    parts.push(plural({ count: targets.providerPools.length, noun: "pool" }));
  }
  if (targets.providers.length > 0) {
    parts.push(plural({ count: targets.providers.length, noun: "provider" }));
  }
  if (parts.length === 0) {
    return MONITORING_COPY.coversNobody;
  }
  return parts.join(" · ");
}

export function describeCoverageGap({
  family,
  stateCodes,
}: {
  family: PsvSourceFamily;
  stateCodes: readonly string[];
}): string {
  const noun =
    family === PsvSourceFamily.StateLicense
      ? "licenses"
      : "Medicaid exclusions";
  return `No automatic source for ${stateCodes.join(", ")} ${noun}`;
}

const SOURCE_LIST_FORMAT = new Intl.ListFormat("en", { type: "conjunction" });

/** Shown beside a disabled save button so it says which sources still need
 * states, since their pickers may be scrolled out of view. */
export function describeMissingStates(
  families: readonly PsvSourceFamily[],
): string {
  const labels = families.map((family) => SOURCE_LABELS[family]);
  return `Choose states for ${SOURCE_LIST_FORMAT.format(labels)}.`;
}
